import React, { PropsWithChildren } from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { blue, common } from '@mui/material/colors';
import { expandedSidebarWidth } from 'constants/common';

export const SidebarStyledContainer = ({
  children,
  isDrawerOpen,
  logoHeight,
  backgroundColor,
}: PropsWithChildren<{ isDrawerOpen: boolean; logoHeight: number; backgroundColor: string }>) => {
  const classes = useStyles({ isDrawerOpen, logoHeight, backgroundColor });

  return <Box className={classes.container}>{children}</Box>;
};

const useStyles = makeStyles<Theme, { isDrawerOpen: boolean; logoHeight: number; backgroundColor: string }>({
  container: {
    height: '100%',
    display: 'grid',
    maxWidth: expandedSidebarWidth,
    overflow: 'hidden',
    color: common.white,
    gridTemplateRows: 'auto 1fr auto',
    background: 'rgba(0, 0, 0, 0.8)',

    scrollbarWidth: 'thin',

    scrollbarColor: 'rgba(255,255,255,0.4) transparent',
    '&::-webkit-scrollbar': {
      width: '8px',
    },

    '&::-webkit-scrollbar-track': {
      background: 'rgba(0,0,0,0.8)',
    },

    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(255,255,255,0.4)',
      borderRadius: '4px',
    },

    '& .logo': {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: '0.5rem 1rem',
      justifyContent: 'center',
      height: ({ isDrawerOpen, logoHeight }) => `${isDrawerOpen ? 'auto' : `${logoHeight}px`}`, // to keep the same height when collapsed

      '& > img': {
        maxWidth: ({ isDrawerOpen }) => `${isDrawerOpen ? '140px' : '32px'}`,
        backgroundColor: ({ backgroundColor }) => backgroundColor,
      },
    },

    '& .navList': {
      padding: ({ isDrawerOpen }) => `0 ${isDrawerOpen ? '0.5rem' : '0'}`,
      scrollbarWidth: ({ isDrawerOpen }) => `${isDrawerOpen ? 'auto' : 'none'}`,
      overflow: 'hidden auto',

      '& .nestedNavList': {
        padding: '0.25rem 0',

        '& .nestedNavListButton': {
          '& .nestedNavListButtonIcon': {
            minWidth: '2rem',
            color: common.white,
          },

          '&:hover': {
            color: common.white,
            borderRadius: '3px',
            backgroundColor: 'rgba(200, 200, 200, 0.2)',
          },
        },

        '& .nestedNavListButtonText': {
          opacity: ({ isDrawerOpen }) => `${isDrawerOpen ? 1 : 0}`,
        },
      },

      '& .userSectionListItem': {
        cursor: 'pointer',
        paddingLeft: '8px',
        flexDirection: ({ isDrawerOpen }) => `${isDrawerOpen ? 'row' : 'column'}`,
        paddingRight: ({ isDrawerOpen }) => `${isDrawerOpen ? '48px' : '8px'}`,

        '& .userAvatar': {
          minWidth: '46px',

          '& .userAvatarImage': {
            width: 35,
            height: 35,
            margin: ({ isDrawerOpen }) => `${!isDrawerOpen ? '0 auto' : '0 3px'}`,
          },
        },

        '& .userNameText': {
          whiteSpace: 'normal',
          display: ({ isDrawerOpen }) => `${isDrawerOpen ? 'block' : 'none'}`,
        },

        '& .MuiListItemSecondaryAction-root': {
          position: ({ isDrawerOpen }) => `${isDrawerOpen ? 'absolute' : 'relative'}`,
          transform: ({ isDrawerOpen }) => `${isDrawerOpen ? 'translateY(-50%)' : 'none'}`,
          marginTop: ({ isDrawerOpen }) => `${isDrawerOpen ? '0' : '8px'}`,

          '& .notificationIconButton': {
            backgroundColor: '#717171',

            '&:hover': {
              backgroundColor: '#717171',
            },
          },
        },
      },
    },

    '& .divider': {
      borderColor: '#ffffff4d',
    },

    '& .active': {
      color: common.white,
      borderRadius: '3px',
      backgroundColor: blue[600],
    },
  },
});
